import {
  ArrayStepperConfig,
  CoverageAccordionSection,
  FormElementConfig,
} from "@platform/design-system";
import get from "lodash/get";
import { FieldPath, FieldValues } from "react-hook-form";

import { formatResponseDate } from "../../../../utils/common";

import { FORM_FIELDS, NO } from "./constants";
import {
  FinalDecisionFormValues,
  InputType,
  ReviewDataResponseCoverage,
  ReviewSection,
} from "./types";
import { UseFinalDecisionFormReturn } from "./useFinalDecisionForm";

// Constant for missing field display
const MISSING_FIELD_VALUE = "missing";

// Constants for submitted chip
const SUBMITTED_CHIP_LABEL = "Submitted";
const SUBMITTED_CHIP_TOOLTIP = `This page displays the most recently submitted decisions. Click "Edit Final Decisions" to make changes or to view any unsubmitted changes made since last submission.`;

/**
 * Checks if the broker won the Broker of Record (BOR) for a prospect.
 * Returns true unless the user explicitly selected "No" (NO = "n").
 * Note: For non-prospects, formState.wonBOR will be undefined, which means
 * this returns true (since undefined !== "n"). Non-prospects are assumed to
 * already have BOR unless explicitly marked as lost via lostBrokerOfRecord.
 */
export const isWonBor = (formState: FinalDecisionFormValues): boolean =>
  formState.wonBOR !== NO;

/**
 * Checks if the broker lost the BOR for an existing client (non-prospect).
 * This checkbox is only shown for non-prospects.
 */
export const isLostBroker = (formState: FinalDecisionFormValues): boolean =>
  formState.lostBrokerOfRecord === true;

/**
 * Checks if the broker is currently the Broker of Record.
 * True when the broker either:
 * - Won BOR for a prospect (wonBOR !== "n"), OR
 * - Is an existing client (wonBOR === undefined)
 * AND has not lost BOR (lostBrokerOfRecord !== true)
 */
export const isBrokerOfRecord = (formState: FinalDecisionFormValues): boolean =>
  isWonBor(formState) && !isLostBroker(formState);

// Field label mapping for review display
const FIELD_LABELS: Record<string, string> = {
  finalDecisionSelected: "Final Decision",
  incumbentProductId: "Incumbent Product Record",
  incumbentAnnualPremium: "Incumbent Annual Premium",
  incumbentContractedCarrier: "Incumbent Contracted Carrier",
  incumbentGaOverridePercentage: "Incumbent GA Override Percentage",
  incumbentGaAnnualOverrideAmount: "Incumbent GA Override Annual Amount",
  winningCarrier: "Winning Carrier",
  winningContractedCarrier: "Contracted Carrier",
  annualWrittenPremium: "Annual Written Premium",
  renewalDate: "Next Renewal Date",
  rateGuarantee: "Rate Guarantee",
  winningGaOverridePercentage: "GA Override Percentage",
  winningGaAnnualOverrideAmount: "GA Annual Override Amount",
  techCredits: "Tech Credits",
  numberOfPlansQuoted: "Number of Plans Quoted",
};

type FormFieldValue = (typeof FORM_FIELDS)[keyof typeof FORM_FIELDS];

export const getParentField = <TFieldValues extends FinalDecisionFormValues>(
  fieldPath: string,
  fieldName: FormFieldValue,
): FieldPath<TFieldValues> =>
  fieldPath.replace(`.${fieldName}`, "") as FieldPath<TFieldValues>;

export const getSectionField = (label: string) =>
  ({
    type: "section",
    label,
    grid: { size: 12 },
  }) as const;

export const getArrayStepperField = (
  name: string,
): ArrayStepperConfig<InputType> => ({
  name,
  type: "arrayStepper",
  grid: { size: 12 },
  // Children will be overridden at call sites
  children: [],
});

export const getFieldValue = ({
  formState,
  fieldPath,
  fieldName,
  searchFieldName,
}: {
  formState: FieldValues;
  fieldPath: string;
  fieldName: FormFieldValue;
  searchFieldName: string;
}): string | boolean | undefined => {
  const prefix = getParentField(fieldPath, fieldName);
  return get(formState, `${prefix}.${searchFieldName}`) as
    | string
    | boolean
    | undefined;
};

export const isVisibleForFinalDecision =
  ({
    fieldName,
    validator,
  }: {
    fieldName: FormFieldValue;
    validator: (
      finalDecision: string,
      incumbentContractedCarrier?: boolean,
      winningContractedCarrier?: boolean,
    ) => boolean;
  }) =>
  (formState: FieldValues, field: FormElementConfig<InputType>): boolean => {
    if (field.type !== "section") {
      const finalDecision = getFieldValue({
        formState,
        fieldName,
        fieldPath: field.name,
        searchFieldName: FORM_FIELDS.FINAL_DECISION_STATUS,
      }) as string | undefined;
      const incumbentContractedCarrier = getFieldValue({
        formState,
        fieldName,
        fieldPath: field.name,
        searchFieldName: FORM_FIELDS.INCUMBENT_CONTRACTED_CARRIER,
      }) as boolean | undefined;
      const winningContractedCarrier = getFieldValue({
        formState,
        fieldName,
        fieldPath: field.name,
        searchFieldName: FORM_FIELDS.WINNING_CONTRACTED_CARRIER,
      }) as boolean | undefined;
      return validator(
        finalDecision,
        incumbentContractedCarrier,
        winningContractedCarrier,
      );
    }

    return false;
  };

export const isVisibleForSold =
  ({
    fieldName,
    validator,
  }: {
    fieldName: FormFieldValue;
    validator?: (sold: boolean, winningContractedCarrier?: boolean) => boolean;
  }) =>
  (formState: FieldValues, field: FormElementConfig<InputType>): boolean => {
    if (field.type !== "section") {
      const sold = getFieldValue({
        formState,
        fieldName,
        fieldPath: field.name,
        searchFieldName: FORM_FIELDS.SOLD,
      });
      const winningContractedCarrier = getFieldValue({
        formState,
        fieldName,
        fieldPath: field.name,
        searchFieldName: FORM_FIELDS.WINNING_CONTRACTED_CARRIER,
      }) as boolean | undefined;
      const isSold = typeof sold === "boolean" ? sold : false;
      if (validator) {
        return validator(isSold, winningContractedCarrier);
      }
      return isSold;
    }

    return false;
  };

export const isVisibleForIncumbentData =
  (fieldName: FormFieldValue) =>
  (formState: FieldValues, field: FormElementConfig<InputType>): boolean => {
    if (field.type !== "section") {
      const incumbent = getFieldValue({
        formState,
        fieldName,
        fieldPath: field.name,
        searchFieldName: FORM_FIELDS.INCUMBENT_PRODUCT_ID,
      });
      return typeof incumbent === "number";
    }

    return false;
  };

export const reviewDataToReviewSections = (
  reviewData: ReturnType<UseFinalDecisionFormReturn["getReviewData"]>,
  includeChip = false,
): ReviewSection[] => {
  const missingOr = <
    C extends { isMissing?: Record<string, boolean | undefined> },
    K extends keyof C,
  >(
    coverage: C,
    missingKey: string,
    valueKey: K,
  ): { value: C[K] | string; isMissing: boolean | undefined } => ({
    value: coverage.isMissing?.[missingKey]
      ? MISSING_FIELD_VALUE
      : coverage[valueKey],
    isMissing: coverage.isMissing?.[missingKey],
  });

  const getWinningDetails = (
    coverage: ReviewDataResponseCoverage,
  ): CoverageAccordionSection["data"][number] => ({
    title: "Winning Details",
    values: [
      {
        label: FIELD_LABELS.winningCarrier,
        ...missingOr(coverage, "winningCarrier", "winningCarrier"),
      },
      {
        label: FIELD_LABELS.winningContractedCarrier,
        value: coverage.winningContractedCarrier,
      },
      {
        label: FIELD_LABELS.rateGuarantee,
        ...missingOr(coverage, "rateGuarantee", "rateGuarantee"),
      },
      {
        label: FIELD_LABELS.renewalDate,
        ...missingOr(coverage, "renewalDate", "renewalDate"),
      },
      {
        label: FIELD_LABELS.annualWrittenPremium,
        ...missingOr(coverage, "annualWrittenPremium", "annualWrittenPremium"),
      },
      {
        label: FIELD_LABELS.winningGaOverridePercentage,
        ...missingOr(
          coverage,
          "winningGaOverridePercentage",
          "winningGaOverridePercentage",
        ),
      },
      {
        label: FIELD_LABELS.winningGaAnnualOverrideAmount,
        ...missingOr(
          coverage,
          "winningGaAnnualOverrideAmount",
          "winningGaAnnualOverrideAmount",
        ),
      },
      {
        label: FIELD_LABELS.techCredits,
        ...missingOr(coverage, "techCredits", "techCredits"),
      },
      {
        label: FIELD_LABELS.numberOfPlansQuoted,
        ...missingOr(coverage, "numberOfPlansQuoted", "numberOfPlansQuoted"),
      },
      { label: "Notes", value: coverage.notes },
    ].filter((item) => item.value), // filter out empty values
  });

  const currentCoveragesSection: ReviewSection = {
    title: "Current Coverages",
    ...(includeChip && {
      chip: {
        label: SUBMITTED_CHIP_LABEL,
        tooltip: SUBMITTED_CHIP_TOOLTIP,
      },
    }),
    sections: reviewData.currentCoverages.map((coverage) => ({
      id: coverage.name,
      headers: [
        coverage.name,
        coverage.isMissing?.winningCarrier
          ? MISSING_FIELD_VALUE
          : coverage.winningCarrier,
        coverage.isMissing?.finalDecisionSelected
          ? MISSING_FIELD_VALUE
          : coverage.finalDecision,
      ],
      isMissingHeaders: {
        0: coverage.isMissing && Object.keys(coverage.isMissing).length > 0,
        1: coverage.isMissing?.winningCarrier,
        2: coverage.isMissing?.finalDecisionSelected,
      },
      data: [
        {
          title: "Incumbent Details",
          values: [
            {
              label: FIELD_LABELS.incumbentProductId,
              value: coverage.isMissing?.incumbentProductId
                ? MISSING_FIELD_VALUE
                : coverage.incumbentProduct,
              isMissing: coverage.isMissing?.incumbentProductId,
            },
            {
              label: FIELD_LABELS.incumbentContractedCarrier,
              value: coverage.incumbentContractedCarrier,
            },
            {
              label: FIELD_LABELS.incumbentAnnualPremium,
              value: coverage.isMissing?.incumbentAnnualPremium
                ? MISSING_FIELD_VALUE
                : coverage.incumbentAnnualPremium,
              isMissing: coverage.isMissing?.incumbentAnnualPremium,
            },
            {
              label: FIELD_LABELS.incumbentGaOverridePercentage,
              value: coverage.isMissing?.incumbentGaOverridePercentage
                ? MISSING_FIELD_VALUE
                : coverage.incumbentGaOverridePercentage,
              isMissing: coverage.isMissing?.incumbentGaOverridePercentage,
            },
            {
              label: FIELD_LABELS.incumbentGaAnnualOverrideAmount,
              value: coverage.isMissing?.incumbentGaAnnualOverrideAmount
                ? MISSING_FIELD_VALUE
                : coverage.incumbentGaAnnualOverrideAmount,
              isMissing: coverage.isMissing?.incumbentGaAnnualOverrideAmount,
            },
          ].filter((item) => item.value), // filter out empty values
        },
        getWinningDetails(coverage),
      ].filter((section) => section.values.length > 0), // filter out empty sections
    })),
  };
  const newCoveragesSection: ReviewSection = {
    title: "New Coverages",
    ...(includeChip &&
      reviewData.currentCoverages.length === 0 && {
        chip: {
          label: SUBMITTED_CHIP_LABEL,
          tooltip: SUBMITTED_CHIP_TOOLTIP,
        },
      }),
    sections: reviewData.newCoverages.map((coverage) => ({
      id: coverage.name,
      headers: [
        coverage.name,
        coverage.isMissing?.winningCarrier
          ? MISSING_FIELD_VALUE
          : coverage.winningCarrier,
        coverage.finalDecision,
      ],
      isMissingHeaders: {
        0: coverage.isMissing && Object.keys(coverage.isMissing).length > 0,
        1: coverage.isMissing?.winningCarrier,
      },
      data: [getWinningDetails(coverage)].filter(
        (section) => section.values.length > 0,
      ), // filter out empty sections
    })),
  };

  return [currentCoveragesSection, newCoveragesSection].filter(
    (section) => section.sections.length > 0,
  );
};

export const calculateNextRenewalDate = (
  effectiveDate: string | null | undefined,
  rateGuaranteeYears: string | undefined,
): Date | null => {
  if (!effectiveDate || !rateGuaranteeYears) {
    return null;
  }

  const years = Number(rateGuaranteeYears);
  if (isNaN(years) || years <= 0) {
    return null;
  }

  const date = formatResponseDate(effectiveDate);
  if (!date || isNaN(date.getTime())) {
    return null;
  }

  date.setFullYear(date.getFullYear() + years);

  return date;
};
